import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";

import { FileDropIcon } from "~/common/redesign-v1";

import { useChatFileDragDrop } from "./use-chat-file-drag-drop";

function ChatFileDropOverlay({
  visible,
  active,
  compact,
}: {
  visible: boolean;
  active: boolean;
  compact: boolean;
}) {
  if (!visible) {
    return null;
  }

  return (
    <div
      className={clsx(
        "tw-pointer-events-none tw-absolute tw-inset-0 tw-z-30 tw-flex tw-flex-col",
        compact ? "tw-p-3" : "tw-p-5",
      )}
      role="region"
      aria-live="polite"
    >
      <div
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[#ACB5BC] tw-mix-blend-multiply"
        aria-hidden
      />
      <div
        className={clsx(
          "tw-relative tw-z-10 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-rounded-[20px] tw-text-center tw-transition-colors tw-duration-150",
          active ? "tw-bg-[#F2F6F9]" : "tw-bg-white",
        )}
      >
        <div
          className={clsx(
            "tw-flex tw-flex-col tw-items-center tw-gap-[9px]",
            compact ? "tw-max-w-[220px] tw-p-5" : "tw-max-w-[446px] tw-px-8 tw-py-10",
          )}
        >
          <FileDropIcon
            className={clsx(
              "tw-block tw-shrink-0 tw-text-primary",
              compact ? "tw-h-9 tw-w-[29px]" : "tw-h-[54px] tw-w-[43px]",
            )}
          />
          <p
            className={clsx(
              "tw-m-0 tw-w-full tw-font-semibold tw-text-black",
              compact ? "tw-text-base tw-leading-5" : "tw-text-xl tw-leading-6",
            )}
          >
            <FormattedMessage id="chat.drop.title" />
          </p>
          <p
            className={clsx(
              "tw-m-0 tw-w-full tw-text-brand-black",
              compact ? "tw-text-xs tw-leading-4" : "tw-text-base tw-leading-[22px]",
            )}
          >
            <FormattedMessage id="chat.drop.description.line1" />
            <br />
            <FormattedMessage id="chat.drop.description.line2" />
          </p>
        </div>
      </div>
    </div>
  );
}

export function ChatFileDropZone({
  disabled,
  onDropFiles,
  children,
  className,
  compact = false,
}: {
  disabled?: boolean;
  onDropFiles: (files: File[]) => void;
  children: React.ReactNode;
  className?: string;
  compact?: boolean;
}) {
  const { visible, active, zoneProps } = useChatFileDragDrop({
    disabled,
    onFiles: onDropFiles,
  });

  return (
    <div
      className={clsx(
        "tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden",
        className,
      )}
      {...zoneProps}
    >
      <div className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
        {children}
      </div>
      <ChatFileDropOverlay visible={visible} active={active} compact={compact} />
    </div>
  );
}
